import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Download, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { newRequestId, savePlayer } from "@/lib/member";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { ProofUpload } from "@/components/member/ProofUpload";

const Row = ({ k, v, cls = "text-white", testid }) => (
  <div className="flex items-center justify-between gap-3 text-xs font-bold text-zinc-400">
    <span>{k}</span>
    <span className={`text-right font-extrabold ${cls}`} data-testid={testid}>{v}</span>
  </div>
);

const downloadQris = async (src, name) => {
  try {
    const blob = await (await fetch(src)).blob();
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: `QRIS-JPSTORE-${name}.${(blob.type.split("/")[1] || "png").replace("jpeg", "jpg")}` });
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("QRIS tersimpan. Buka aplikasi e-wallet / m-banking lalu scan dari galeri.");
  } catch {
    window.open(src, "_blank", "noopener");
  }
};

export const ConfirmPurchaseDialog = ({ product, gameId, wa, pkg, onClose, nickname }) => {
  const nav = useNavigate();
  const { qris_mode, qris_image: qrisAll, qris_images } = useSiteSettings();
  const qris_image = (qris_mode === "per_game" && qris_images?.[product.id]) || qrisAll;
  const [proofId, setProofId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requestId] = useState(newRequestId);

  const submit = async () => {
    if (!proofId) return toast.error("Upload bukti transfer terlebih dahulu");
    setBusy(true);
    try {
      await api.post("/orders", { product_id: product.id, game_id: gameId, wa_number: wa, price: pkg.price, proof_id: proofId, client_request_id: requestId });
      savePlayer(product.id, gameId, wa);
      toast.success("Transaksi terkirim! Status PENDING — sedang menunggu pemeriksaan admin.", { duration: 6000 });
      nav("/");
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim transaksi, coba lagi"));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-[#3a3220] bg-[#0f0f0f] text-white sm:max-w-[440px]" data-testid="confirm-purchase-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="kz-display text-xl text-[var(--jp-primary)]">Konfirmasi Pembelian</DialogTitle>
          <DialogDescription className="text-xs font-bold text-zinc-400">
            Pastikan data pemain dan produk yang Anda pilih sudah benar sebelum melanjutkan pembayaran.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 rounded-xl border border-[#3a3220] bg-[#161616] p-4">
          <Row k="Game" v={product.name} testid="confirm-game" />
          <Row k={product.id_label} v={gameId} testid="confirm-game-id" />
          {nickname ? <Row k="Nickname" v={<span className="inline-flex items-center gap-1"><BadgeCheck size={14} /> {nickname}</span>} cls="text-emerald-400" testid="confirm-nickname" /> : null}
          <Row k="WhatsApp" v={wa} testid="confirm-wa" />
          <Row k="Nominal Chip" v={pkg.label} cls="text-[var(--jp-primary)]" testid="confirm-chip" />
          <div className="flex items-center justify-between border-t border-white/10 pt-2">
            <span className="text-sm font-bold text-zinc-400">Harga</span>
            <span className="text-lg font-extrabold" data-testid="confirm-price">{formatRupiah(pkg.price)}</span>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="rounded-2xl bg-white p-3" data-testid="qris-code-container">
            <img src={qris_image || "/qris-placeholder.png"} alt="QRIS" className="h-56 w-56 object-contain" data-testid="qris-image" />
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-zinc-400">
            <QrCode size={12} /> Scan QRIS lalu upload bukti transfer di bawah
          </div>
          {qris_image ? (
            <button type="button" onClick={() => downloadQris(qris_image, product.id === "royal_dream" ? "ROYALDREAM" : "HIGGSDOMINO")}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#e5b84a] px-4 py-1.5 text-xs font-extrabold text-[#e5b84a] transition-colors hover:bg-[#e5b84a] hover:text-black" data-testid="qris-download-btn">
              <Download size={13} /> Download QRIS
            </button>
          ) : null}
        </div>
        <ProofUpload kind="topup" onChange={setProofId} testid="topup-proof" />
        {proofId ? (
          <button onClick={submit} disabled={busy} className="kz-gold-btn w-full rounded-full py-3 text-sm font-extrabold text-black disabled:opacity-60" data-testid="sudah-bayar-btn">
            {busy ? <Loader2 size={16} className="mx-auto animate-spin" /> : "SUDAH BAYAR"}
          </button>
        ) : (
          <p className="text-center text-[11px] font-bold text-zinc-500" data-testid="sudah-bayar-hint">Tombol "Sudah Bayar" muncul setelah bukti transfer tersimpan.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
